/**
 * MailboxModal.ts
 * 
 * Interactive Modal for Red Mailbox:
 * - Tab 1: 📬 우편함 (System, NPC & Player Letters with attached gold)
 * - Tab 2: 🏛️ 집세/세금 관리소 (Tax Safe with MoneySelectorModal C/S/G/P deposit)
 */

import { Currency } from '../../core/Currency';
import { EntityState } from '../../core/Types';
import { housingSystem } from '../world/HousingSystem';
import { MailboxTaxData } from '../world/HousingData';
import { EventBus } from '../../core/EventBus';

export class MailboxModal {
  private el: HTMLElement | null = null;
  private currentZoneId = 1;
  private currentX = 0;
  private currentY = 0;
  private player: EntityState | null = null;
  private activeTab: 'letters' | 'tax' = 'letters';

  public open(zoneId: number, x: number, y: number, player: EntityState): void {
    this.currentZoneId = zoneId;
    this.currentX = x;
    this.currentY = y;
    this.player = player;

    if (!this.el) {
      this.buildDOM();
    }
    this.render();
    if (this.el) {
      this.el.style.display = 'flex';
    }
  }

  public close(): void {
    if (this.el) {
      this.el.style.display = 'none';
    }
  }

  private buildDOM(): void {
    this.el = document.createElement('div');
    this.el.id = 'mailbox-modal-container';
    this.el.style.cssText = `
      position: fixed;
      inset: 0;
      background: rgba(0, 0, 0, 0.75);
      z-index: 10050;
      display: none;
      align-items: center;
      justify-content: center;
      font-family: 'Pretendard', sans-serif;
      backdrop-filter: blur(4px);
    `;
    document.body.appendChild(this.el);
  }

  private render(): void {
    if (!this.el || !this.player) return;

    const mb = housingSystem.getMailboxAt(this.currentZoneId, this.currentX, this.currentY);
    const letters = mb?.letters || [];
    const depositedCopper = mb?.depositedCopper || 0;
    const playerCopper = this.player.gold ?? 0;

    this.el.innerHTML = `
      <div style="
        background: #111827;
        border: 2px solid #ef4444;
        border-radius: 12px;
        width: 520px;
        max-width: 95vw;
        color: #f8fafc;
        box-shadow: 0 16px 40px rgba(239, 68, 68, 0.3);
        overflow: hidden;
      ">
        <!-- HEADER -->
        <div style="background: linear-gradient(135deg, #b91c1c, #991b1b); padding: 14px 18px; display: flex; justify-content: space-between; align-items: center; border-bottom: 2px solid #ef4444;">
          <div style="font-weight: 800; font-size: 1.15rem; display: flex; align-items: center; gap: 8px;">
            📮 빨간색 우체통 & 집세 금고
          </div>
          <button id="mb-btn-close" style="background: none; border: none; color: #fecaca; font-size: 1.4rem; cursor: pointer; line-height: 1;">&times;</button>
        </div>

        <!-- TABS -->
        <div style="display: flex; background: #1e293b; border-bottom: 1px solid #334155;">
          <button id="mb-tab-letters" style="
            flex: 1; padding: 10px; border: none; font-weight: 700; cursor: pointer;
            background: ${this.activeTab === 'letters' ? '#334155' : 'transparent'};
            color: ${this.activeTab === 'letters' ? '#f87171' : '#94a3b8'};
            border-bottom: 2px solid ${this.activeTab === 'letters' ? '#ef4444' : 'transparent'};
          ">📬 우편함 (${letters.length})</button>
          <button id="mb-tab-tax" style="
            flex: 1; padding: 10px; border: none; font-weight: 700; cursor: pointer;
            background: ${this.activeTab === 'tax' ? '#334155' : 'transparent'};
            color: ${this.activeTab === 'tax' ? '#facc15' : '#94a3b8'};
            border-bottom: 2px solid ${this.activeTab === 'tax' ? '#f59e0b' : 'transparent'};
          ">🏛️ 집세(세금) 금고</button>
        </div>

        <!-- CONTENT BODY -->
        <div style="padding: 16px; min-height: 280px; max-height: 420px; overflow-y: auto;">
          ${this.activeTab === 'letters' ? this.renderLettersTab(letters) : this.renderTaxTab(depositedCopper, playerCopper)}
        </div>
      </div>
    `;

    this.bindEvents();
  }

  private renderLettersTab(letters: MailboxTaxData['letters']): string {
    if (letters.length === 0) {
      return `<div style="text-align: center; color: #64748b; padding: 40px 0;">도착한 우편이 없습니다.</div>`;
    }

    return `
      <div style="display: flex; flex-direction: column; gap: 10px;">
        ${letters
          .map(
            (l, idx) => `
          <div style="background: #1e293b; border: 1px solid #334155; border-radius: 8px; padding: 12px;">
            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 6px;">
              <span style="font-weight: 700; color: #f87171;">✉️ ${l.title}</span>
              <span style="font-size: 0.75rem; color: #94a3b8;">발신: ${l.sender}</span>
            </div>
            <div style="font-size: 0.85rem; color: #cbd5e1; line-height: 1.4; margin-bottom: 8px;">${l.content}</div>
            <div style="display: flex; justify-content: space-between; align-items: center; padding-top: 6px; border-top: 1px dashed #334155;">
              <span style="font-size: 0.8rem; color: #facc15;">첨부 골드: 🪙${l.goldAttached} Gold</span>
              <button data-claim-letter="${idx}" style="background: #2563eb; color: #fff; border: none; padding: 4px 10px; border-radius: 4px; font-size: 0.75rem; font-weight: 700; cursor: pointer;">
                수령
              </button>
            </div>
          </div>
        `
          )
          .join('')}
      </div>
    `;
  }

  private renderTaxTab(depositedCopper: number, playerCopper: number): string {
    const depositedBreakdown = Currency.breakdown(depositedCopper);
    const playerBreakdown = Currency.breakdown(playerCopper);

    return `
      <div style="display: flex; flex-direction: column; gap: 14px;">
        <div style="background: #1e293b; border: 1px solid #334155; border-radius: 8px; padding: 14px;">
          <div style="font-size: 0.85rem; color: #94a3b8; margin-bottom: 4px;">🏛️ 현재 우체통 집세 예치금:</div>
          <div style="font-size: 1.25rem; font-weight: 800; color: #facc15;">
            🪙 ${depositedBreakdown.platinum}P ${depositedBreakdown.gold}G ${depositedBreakdown.silver}S ${depositedBreakdown.copper}C
          </div>
          <div style="font-size: 0.75rem; color: #64748b; margin-top: 4px;">
            매 주기마다 토지 면적에 비례한 주택 유지세가 금고에서 자동 납부됩니다.
          </div>
        </div>

        <div style="background: #1e293b; border: 1px solid #334155; border-radius: 8px; padding: 14px;">
          <div style="font-size: 0.85rem; color: #94a3b8; margin-bottom: 4px;">🎒 플레이어 보유 자금:</div>
          <div style="font-size: 1.1rem; font-weight: 700; color: #38bdf8;">
            🪙 ${playerBreakdown.platinum}P ${playerBreakdown.gold}G ${playerBreakdown.silver}S ${playerBreakdown.copper}C
          </div>
        </div>

        <!-- QUICK DEPOSIT BUTTONS -->
        <div style="display: flex; flex-direction: column; gap: 8px;">
          <div style="font-size: 0.8rem; font-weight: 700; color: #cbd5e1;">집세 금고에 자금 예치하기:</div>
          <div style="display: grid; grid-template-columns: repeat(4, 1fr); gap: 6px;">
            <button data-tax-deposit="1000" style="background: #334155; color: #f8fafc; border: 1px solid #475569; padding: 8px; border-radius: 6px; font-weight: 700; cursor: pointer; font-size: 0.8rem;">+1 Silver</button>
            <button data-tax-deposit="10000" style="background: #334155; color: #f8fafc; border: 1px solid #475569; padding: 8px; border-radius: 6px; font-weight: 700; cursor: pointer; font-size: 0.8rem;">+10 Silver</button>
            <button data-tax-deposit="1000000" style="background: #d97706; color: #fff; border: 1px solid #b45309; padding: 8px; border-radius: 6px; font-weight: 700; cursor: pointer; font-size: 0.8rem;">+1 Gold</button>
            <button data-tax-deposit="10000000" style="background: #b45309; color: #fff; border: 1px solid #78350f; padding: 8px; border-radius: 6px; font-weight: 700; cursor: pointer; font-size: 0.8rem;">+10 Gold</button>
          </div>
        </div>
      </div>
    `;
  }

  private bindEvents(): void {
    if (!this.el || !this.player) return;

    this.el.querySelector('#mb-btn-close')?.addEventListener('click', () => this.close());
    this.el.querySelector('#mb-tab-letters')?.addEventListener('click', () => {
      this.activeTab = 'letters';
      this.render();
    });
    this.el.querySelector('#mb-tab-tax')?.addEventListener('click', () => {
      this.activeTab = 'tax';
      this.render();
    });

    // Claim letters
    this.el.querySelectorAll('[data-claim-letter]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        const idx = parseInt((e.currentTarget as HTMLElement).getAttribute('data-claim-letter') || '0', 10);
        const mb = housingSystem.getMailboxAt(this.currentZoneId, this.currentX, this.currentY);
        if (mb && mb.letters[idx] && this.player) {
          const letter = mb.letters.splice(idx, 1)[0];
          const goldCopper = Currency.toCopper({ gold: letter.goldAttached });
          this.player.gold = (this.player.gold ?? 0) + goldCopper;
          EventBus.emit('LOG_EMITTED', {
            id: Math.random().toString(),
            timestamp: Date.now(),
            text: `📮 [우체통] '${letter.title}' 우편을 확인하고 첨부된 ${letter.goldAttached} Gold를 수령했습니다!`,
            category: 'system',
          });
          this.render();
        }
      });
    });

    // Deposit tax
    this.el.querySelectorAll('[data-tax-deposit]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        const amount = parseInt((e.currentTarget as HTMLElement).getAttribute('data-tax-deposit') || '0', 10);
        if (this.player && amount > 0) {
          const res = housingSystem.depositTaxToMailbox(
            this.currentZoneId,
            this.currentX,
            this.currentY,
            this.player,
            amount
          );
          if (res.success) {
            EventBus.emit('LOG_EMITTED', {
              id: Math.random().toString(),
              timestamp: Date.now(),
              text: `🏛️ [우체통 집세 예치] ${res.message}`,
              category: 'system',
            });
            this.render();
          } else {
            alert(res.message);
          }
        }
      });
    });
  }
}

export const mailboxModalInstance = new MailboxModal();
